import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useArealplan, useUpdateArealplan } from '@origo/planregister';
import { Loader, Notice } from '@origo/origo-ui';
import { useToast } from '@origo/origo-components';
import PlanInfoPage from './PlanInfoPage';
import { useCodelistOptions } from '../../hooks/useCodelistOptions';
import { applyPlanInfoFormToArealplan, toPlanInfoFormValues } from '../../mappers/toPlanInfo';
import type { PlanInfoFormValues } from '../../types';

interface PlanInfoContainerProps {
    napId: string;
    /** Lar shell-en speile faneflukt sin dirty-state for «Lagre plan»-knappen. */
    onDirtyChange?: (isDirty: boolean) => void;
    /** Registrerer save-handleren med shell-en så «Lagre plan» kan trigge samme save. */
    onSaveHandlerChange?: (handler: (() => void) | null) => void;
}

const PlanInfoContainer: React.FC<PlanInfoContainerProps> = ({
    napId,
    onDirtyChange,
    onSaveHandlerChange,
}) => {
    const { t } = useTranslation();
    const toast = useToast();
    const { data: plan, isLoading, isError } = useArealplan(napId);
    const codelists = useCodelistOptions();
    const update = useUpdateArealplan();
    const [draft, setDraft] = useState<PlanInfoFormValues | null>(null);

    const initialValues = useMemo(
        () => (plan ? toPlanInfoFormValues(plan) : null),
        [plan]
    );

    useEffect(() => {
        if (initialValues) setDraft(initialValues);
    }, [initialValues]);

    const isDirty = useMemo(() => {
        if (!initialValues || !draft) return false;
        return (Object.keys(initialValues) as Array<keyof PlanInfoFormValues>)
            .some(k => draft[k] !== initialValues[k]);
    }, [draft, initialValues]);

    const handleSave = useCallback(() => {
        if (!plan || !draft) return;
        toast.execute(
            () => update.mutateAsync(applyPlanInfoFormToArealplan(plan, draft)),
            { success: t('common.saved'), error: t('common.errorSaving') }
        );
    }, [toast, t, update, plan, draft]);

    const handleDiscard = useCallback(() => {
        if (initialValues) setDraft(initialValues);
    }, [initialValues]);

    // Shell-knappen «Lagre plan» speiler aktiv fane sin dirty-state.
    useEffect(() => {
        onDirtyChange?.(isDirty);
        return () => onDirtyChange?.(false);
    }, [isDirty, onDirtyChange]);

    // Registrer save-handleren med shell-en kun når det faktisk er endringer
    // å lagre. Cleanup nuller ut ved unmount/tab-skifte.
    useEffect(() => {
        onSaveHandlerChange?.(isDirty ? handleSave : null);
        return () => onSaveHandlerChange?.(null);
    }, [isDirty, handleSave, onSaveHandlerChange]);

    if (isLoading || !draft || codelists.isLoading) {
        return <Loader label={t('common.loading')} />;
    }
    if (isError) {
        return <Notice variant="danger">{t('common.errorLoading')}</Notice>;
    }
    if (!plan) {
        return <Notice variant="info">{t('plan.notFound')}</Notice>;
    }

    return (
        <PlanInfoPage
            values={draft}
            plantypeOptions={codelists.plantype}
            planstatusOptions={codelists.planstatus}
            lovreferanseOptions={codelists.lovreferanse}
            onChange={(field, value) => setDraft(d => (d ? { ...d, [field]: value } : d))}
            onCancel={handleDiscard}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={update.isPending}
        />
    );
};

export default PlanInfoContainer;
